import React, { useState } from 'react';
import { Event } from '../../types/Event';
import { UI_THEME } from '../../config/constants';
import ScoreArc from '../ui/ScoreArc';

interface EventCardProps {
  event: Event;
}

const TYPE_COLORS: Record<string, string> = {
  conference: '#0ea5e9',
  summit: '#8b5cf6',
  meetup: '#10b981',
  workshop: '#f59e0b',
  hackathon: '#ef4444',
  expo: '#ec4899',
};

const SCORE_LABELS: { key: keyof Event['scores']; label: string }[] = [
  { key: 'audience_fit', label: 'Audience Fit' },
  { key: 'networking_quality', label: 'Networking' },
  { key: 'lead_generation_potential', label: 'Lead Gen' },
  { key: 'content_relevance', label: 'Content' },
];

function scoreColor(score: number): string {
  if (score >= 7) return UI_THEME.colors.success;
  if (score >= 5) return UI_THEME.colors.warning;
  return UI_THEME.colors.error;
}

function formatEventDate(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysUntil(isoDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(isoDate + 'T00:00:00');
  return Math.ceil((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const days = daysUntil(event.event_date);
  const typeColor = TYPE_COLORS[event.type] || '#6b7280';
  const overallColor = scoreColor(event.overall_score);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: UI_THEME.borderRadius.md,
        border: `1px solid ${overallColor}22`,
        borderLeft: `4px solid ${overallColor}`,
        boxShadow: UI_THEME.shadows.card,
        transition: 'all 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{ padding: '20px 24px', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          {/* Left: name, type badge, location, date */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: UI_THEME.colors.gray[800] }}>
                {event.name}
              </h3>
              <span
                style={{
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  backgroundColor: `${typeColor}15`,
                  color: typeColor,
                  border: `1px solid ${typeColor}30`,
                }}
              >
                {event.type}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
              {/* Date */}
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: UI_THEME.colors.gray[600] }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatEventDate(event.event_date)}
              </span>

              {/* Location */}
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: UI_THEME.colors.gray[600] }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {event.location.city}, {event.location.country}
                {event.location.venue && ` — ${event.location.venue}`}
              </span>

              {/* Days until */}
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  backgroundColor: days <= 7 ? '#fef2f2' : days <= 30 ? '#fffbeb' : '#f0fdf4',
                  color: days <= 7 ? '#dc2626' : days <= 30 ? '#d97706' : '#16a34a',
                }}
              >
                {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`}
              </span>

              {/* Attendance size */}
              {event.attendance_size && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: UI_THEME.colors.gray[500] }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {event.attendance_size}
                </span>
              )}
            </div>
          </div>

          {/* Right: overall score + expand arrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            {/* Score mini-bars */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {SCORE_LABELS.map(({ key, label }) => (
                <div key={key} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: `${scoreColor(event.scores[key])}15`,
                    border: `1px solid ${scoreColor(event.scores[key])}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: scoreColor(event.scores[key]),
                  }}>
                    {event.scores[key]}
                  </div>
                  <div style={{ fontSize: '9px', color: UI_THEME.colors.gray[400], marginTop: '2px', lineHeight: 1 }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>

            {/* Overall score arc — reuses the shared ScoreArc component */}
            <ScoreArc score={event.overall_score} size={56} strokeWidth={6} />

            {/* Expand arrow */}
            <svg
              width="20"
              height="20"
              fill="none"
              stroke={UI_THEME.colors.gray[400]}
              viewBox="0 0 24 24"
              style={{ transition: 'transform 0.2s ease', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 24px 20px', borderTop: `1px solid ${UI_THEME.colors.gray[200]}` }}>
          {/* Description */}
          <div style={{ marginTop: '16px' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '600', color: UI_THEME.colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Description
            </h4>
            <p style={{ margin: 0, fontSize: '14px', color: UI_THEME.colors.gray[700], lineHeight: 1.6 }}>
              {event.description}
            </p>
          </div>

          {/* Recommendation */}
          <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <h4 style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '600', color: '#15803d' }}>
              Recommendation
            </h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#166534', lineHeight: 1.5 }}>
              {event.recommendation}
            </p>
          </div>

          {/* Notable speakers / companies */}
          {event.notable_speakers_or_companies.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: UI_THEME.colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Notable Speakers & Companies
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {event.notable_speakers_or_companies.map((item, i) => (
                  <span
                    key={i}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      backgroundColor: UI_THEME.colors.gray[100],
                      color: UI_THEME.colors.gray[700],
                      border: `1px solid ${UI_THEME.colors.gray[200]}`,
                    }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Website link */}
          {event.website && (
            <div style={{ marginTop: '16px' }}>
              <a
                href={event.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: UI_THEME.colors.primary,
                  backgroundColor: `${UI_THEME.colors.primary}10`,
                  border: `1px solid ${UI_THEME.colors.primary}30`,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Visit Website
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCard;
